import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Briefcase } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function BusinessProfileSetup() {
  const navigate = useNavigate();
  const { profileId } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(!!profileId);
  const [formData, setFormData] = useState({
    business_name: '',
    industry: '',
    tone_preference: 'Conversational',
    sender_name: '',
    sender_role: 'Owner'
  });

  useEffect(() => {
    if (profileId) {
      loadProfile();
    }
  }, [profileId]);

  const loadProfile = async () => {
    try {
      const response = await axios.get(`${API}/email-campaigns/profiles`);
      const profiles = response.data.profiles || [];
      const profile = profiles.find(p => p.profile_id === profileId);
      
      if (profile) {
        setFormData({
          business_name: profile.business_name,
          industry: profile.industry,
          tone_preference: profile.tone_preference,
          sender_name: profile.sender_name,
          sender_role: profile.sender_role || 'Owner'
        });
      } else {
        toast.error('Profile not found');
        navigate('/email-campaigns');
      }
    } catch (error) {
      console.error('Load profile error:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.business_name || !formData.industry || !formData.sender_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/email-campaigns/profiles`, formData);
      toast.success(response.data.message);
      navigate('/email-campaigns');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/email-campaigns')}
          className="mb-4 text-white/60 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-[#00d4aa]/20 rounded-lg">
            <Briefcase className="w-6 h-6 text-[#00d4aa]" />
          </div>
          <h1 className="text-3xl font-bold text-white">
            {profileId ? 'Edit' : 'Create'} Business Profile
          </h1>
        </div>
        <p className="text-white/60">
          {profileId 
            ? 'Update your business profile information'
            : 'Set up your business profile to personalize your email campaigns'}
        </p>
      </div>

      {/* Form */}
      <Card className="card">
        <CardHeader>
          <CardTitle>Profile Information</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Name */}
            <div>
              <Label htmlFor="business_name" className="text-white mb-2">
                Business Name *
              </Label>
              <Input
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                placeholder="e.g., Sparky Digital Marketing"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            {/* Industry */}
            <div>
              <Label htmlFor="industry" className="text-white mb-2">
                Industry *
              </Label>
              <Input
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                placeholder="e.g., Electrical Contracting, Digital Marketing"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            {/* Tone Preference */}
            <div>
              <Label htmlFor="tone_preference" className="text-white mb-2">
                Email Tone *
              </Label>
              <select
                id="tone_preference"
                name="tone_preference"
                value={formData.tone_preference}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d4aa]"
              >
                <option value="Professional">Professional</option>
                <option value="Conversational">Conversational</option>
                <option value="Direct">Direct</option>
                <option value="Friendly">Friendly</option>
              </select>
              <p className="text-white/40 text-sm mt-1">
                How formal should your emails sound?
              </p>
            </div>

            {/* Sender Name */}
            <div>
              <Label htmlFor="sender_name" className="text-white mb-2">
                Sender Name *
              </Label>
              <Input
                id="sender_name"
                name="sender_name"
                value={formData.sender_name}
                onChange={handleChange}
                placeholder="e.g., John Smith"
                className="bg-white/5 border-white/10 text-white"
              />
              <p className="text-white/40 text-sm mt-1">
                The person sending these emails
              </p>
            </div>

            {/* Sender Role */}
            <div>
              <Label htmlFor="sender_role" className="text-white mb-2">
                Sender Role
              </Label>
              <Input
                id="sender_role"
                name="sender_role"
                value={formData.sender_role}
                onChange={handleChange}
                placeholder="e.g., Owner, Marketing Director"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/email-campaigns')}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {profileId ? 'Update Profile' : 'Save Profile'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default BusinessProfileSetup;
